import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Zap, Crown, Globe, Users, User, Lock, Star } from 'lucide-react';

const Pricing = () => {
  const [serviceType, setServiceType] = useState('restaurant');
  const [activeIndex, setActiveIndex] = useState(1); // Default to the middle highlighted card
  const carouselRef = useRef(null);
  const brandGold = "#ffb700";

  // Configuration for plans
  const plans = {
    restaurant: [
      {
        name: "Weekly Plan",
        price: "$99",
        cycle: "/week",
        desc: "Perfect for pop-ups & seasonal spikes.",
        icon: Zap,
        features: ["Unlimited AI Voice Orders", "Real-time Inventory", "Basic Analytics", "Email Support"],
        highlight: false
      },
      {
        name: "Monthly Plan",
        price: "$349",
        cycle: "/month",
        desc: "Standard for established venues.",
        icon: Star,
        features: ["Everything in Weekly", "Full Analytics Suite", "Kitchen Display Sync", "Priority Support"],
        highlight: true // Highlights the middle card
      },
      {
        name: "Yearly Plan",
        price: "$2999",
        cycle: "/year",
        desc: "Maximum savings for enterprise.",
        icon: Crown,
        features: ["Everything in Monthly", "Multi-Location Support", "Dedicated Account Manager", "API Access"],
        highlight: false
      }
    ],
    clinic: [
      {
        name: "Clinic",
        price: "$199",
        cycle: "/mo",
        desc: "Essential neural nodes for private practice.",
        icon: User,
        features: ["1 Neural Voice Node", "Basic Admin Dashboard", "WhatsApp Integration", "Email Support"],
        highlight: false
      },
      {
        name: "Hospital",
        price: "$499",
        cycle: "/mo",
        desc: "Full command center for facilities.",
        icon: Users,
        features: ["3 Neural Voice Nodes", "Full Command Center", "Patient Mobile App", "EHR/EMR Integration", "24/7 Priority Support"],
        highlight: true
      },
      {
        name: "Network",
        price: "Custom",
        cycle: "",
        desc: "For regional healthcare networks.",
        icon: Globe,
        features: ["Unlimited Voice Nodes", "Custom AI Model Training", "White-Label Solution", "Dedicated Server Core", "SLA Guarantee"],
        highlight: false
      }
    ]
  };

  // Center the highlighted card automatically on mobile when service type changes
  useEffect(() => {
    if (window.innerWidth < 768 && carouselRef.current) {
      // Small timeout to allow DOM to render the new cards before scrolling
      setTimeout(() => {
        if (carouselRef.current) {
          const cardWidth = window.innerWidth * 0.85; // Approximate width of the 85vw card
          carouselRef.current.scrollTo({ left: cardWidth, behavior: 'smooth' });
          setActiveIndex(1);
        }
      }, 100);
    }
  }, [serviceType]);

  // Handle scroll to update the active dot
  const handleScroll = () => {
    if (!carouselRef.current) return;
    const scrollLeft = carouselRef.current.scrollLeft;
    // Calculate which card is currently centered based on scroll position
    const cardWidthEstimate = window.innerWidth * 0.85; 
    const newIndex = Math.round(scrollLeft / cardWidthEstimate);
    if (newIndex !== activeIndex && newIndex >= 0 && newIndex < 3) {
      setActiveIndex(newIndex);
    }
  };

  return (
    <div className="pt-32 pb-20 px-4 md:px-12 bg-[#020408] min-h-screen overflow-hidden">
      <div className="max-w-7xl mx-auto text-center mb-16 relative z-10">
        <h2 className="text-4xl md:text-5xl font-black mb-6 text-white tracking-tighter">
          Scalable <span style={{ color: brandGold }}>Investment.</span>
        </h2>
        <p className="text-slate-400 text-base md:text-lg mb-10 px-4">
          Deploy the Stark X Neural Core. Choose the capacity that fits your scale.
        </p>

        {/* SERVICE TYPE TOGGLE */}
        <div className="flex bg-slate-900/50 p-1.5 rounded-2xl border border-white/5 w-fit mx-auto mb-10 md:mb-16">
          <button 
            onClick={() => setServiceType('restaurant')}
            className={`px-6 md:px-8 py-3 rounded-xl font-bold text-sm md:text-base transition-all ${serviceType === 'restaurant' ? 'bg-[#ffb700] text-black shadow-lg shadow-[#ffb700]/20' : 'text-slate-400 hover:text-white'}`}
          >
            Restaurant
          </button>
          <button 
            onClick={() => setServiceType('clinic')}
            className={`px-6 md:px-8 py-3 rounded-xl font-bold text-sm md:text-base transition-all ${serviceType === 'clinic' ? 'bg-cyan-500 text-white shadow-lg shadow-cyan-500/20' : 'text-slate-400 hover:text-white'}`}
          >
            Clinic
          </button>
        </div>

        {/* PLANS GRID / CAROUSEL */}
        <div 
          ref={carouselRef}
          onScroll={handleScroll}
          className="flex overflow-x-auto snap-x snap-mandatory hide-scrollbar md:grid md:grid-cols-3 gap-4 md:gap-8 items-start w-full px-2 md:px-0 pb-4 md:pb-0"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          <style>{`.hide-scrollbar::-webkit-scrollbar { display: none; }`}</style>
          
          <AnimatePresence mode="wait">
            {plans[serviceType].map((plan, index) => (
              <motion.div
                key={plan.name + serviceType}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className={`min-w-[85vw] md:min-w-0 snap-center shrink-0 relative p-8 rounded-[2.5rem] border flex flex-col text-left h-full transition-all duration-300 group
                  ${plan.highlight 
                    ? (serviceType === 'restaurant' ? 'bg-[#ffb700]/5 border-[#ffb700] shadow-[0_0_50px_rgba(255,183,0,0.15)] md:scale-105 z-10' : 'bg-cyan-500/5 border-cyan-500 shadow-[0_0_50px_rgba(6,182,212,0.15)] md:scale-105 z-10') 
                    : 'bg-slate-900/40 border-white/10 md:hover:border-white/20'
                  }
                `}
              >
                {/* Highlight Badge */}
                {plan.highlight && (
                  <div className={`absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-[10px] font-black uppercase tracking-widest text-black ${serviceType === 'restaurant' ? 'bg-[#ffb700]' : 'bg-cyan-500'}`}>
                    Most Popular
                  </div>
                )}

                {/* Header */}
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 transition-colors ${serviceType === 'restaurant' ? 'bg-[#ffb700]/10 text-[#ffb700]' : 'bg-cyan-500/10 text-cyan-400'}`}>
                  <plan.icon size={28} />
                </div>
                
                <h3 className="text-xl font-black text-white uppercase tracking-wider mb-2">{plan.name}</h3>
                <p className="text-slate-500 text-sm h-10 mb-6 leading-tight">{plan.desc}</p>

                {/* Price */}
                <div className="mb-8">
                  {plan.price === "Custom" ? (
                    <span className="text-5xl font-black text-white tracking-tighter">Custom</span>
                  ) : (
                    <>
                      <span className="text-5xl font-black text-white tracking-tighter">{plan.price}</span>
                      <span className={`text-sm font-bold ml-1 ${serviceType === 'restaurant' ? 'text-[#ffb700]' : 'text-cyan-400'}`}>{plan.cycle}</span>
                    </>
                  )}
                </div>

                {/* Divider */}
                <div className="w-full h-px bg-white/10 mb-8" />

                {/* Features */}
                <ul className="space-y-4 mb-10 flex-grow">
                  {plan.features.map((feat, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-slate-300">
                      <Check size={16} className={`mt-0.5 shrink-0 ${serviceType === 'restaurant' ? 'text-[#ffb700]' : 'text-cyan-400'}`} />
                      <span className="leading-snug">{feat}</span>
                    </li>
                  ))}
                </ul>

                {/* Button / Dynamic Redirect Link */}
                <a 
                  href={
                    plan.price === "Custom" 
                      ? "mailto:info@starkxai.com" 
                      : serviceType === 'restaurant' 
                        ? "https://starkxrestoservices.onrender.com/register" 
                        : "https://starkxaihealth.onrender.com/register"
                  }
                  className={`block text-center w-full py-4 rounded-xl font-bold text-sm uppercase tracking-widest transition-all ${
                    plan.highlight 
                      ? (serviceType === 'restaurant' ? 'bg-[#ffb700] text-black hover:bg-white shadow-[0_0_20px_rgba(255,183,0,0.3)]' : 'bg-cyan-500 text-black hover:bg-white shadow-[0_0_20px_rgba(6,182,212,0.3)]')
                      : 'bg-white/10 text-white hover:bg-white hover:text-black'
                  }`}
                >
                  {plan.price === "Custom" ? "Contact Us" : "Activate Core"}
                </a>

                {/* Security Note */}
                <div className="mt-4 flex justify-center items-center gap-2 opacity-40">
                  <Lock size={10} /> 
                  <span className="text-[10px] uppercase font-bold tracking-wider">
                    {serviceType === 'restaurant' ? 'Stripe Secured' : 'HIPAA Encrypted'}
                  </span>
                </div>

              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* DOTS PAGINATION (Mobile Only) */}
        <div className="flex md:hidden justify-center items-center gap-3 mt-8">
          {plans[serviceType].map((_, index) => (
            <div 
              key={index}
              className={`h-2 rounded-full transition-all duration-300 ${
                activeIndex === index 
                  ? (serviceType === 'restaurant' ? 'w-8 bg-[#ffb700]' : 'w-8 bg-cyan-500')
                  : 'w-2 bg-white/20'
              }`}
            />
          ))}
        </div>

      </div>
    </div>
  );
};

export default Pricing;